import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Globe, ShieldCheck, ArrowUpRight } from 'lucide-react';

export const Footer: React.FC = () => {
  return (
    <footer
      style={{
        backgroundColor: 'var(--bg-deep)',
        borderTop: '1px solid var(--border-medium)',
        paddingTop: '64px',
        paddingBottom: '40px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '-100px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '200px',
          background: 'radial-gradient(circle, var(--brand-red-glow-subtle) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="container-max" style={{ position: 'relative', zIndex: 1 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '40px',
            marginBottom: '48px',
          }}
        >
          {/* Col 1: Brand & Identity */}
          <div style={{ maxWidth: '360px' }}>
            <Link to="/" style={{ display: 'inline-block', marginBottom: '16px' }}>
              <img
                src="/logo.png"
                alt="Forty2 Logo"
                className="brand-logo-img"
                style={{ height: '36px' }}
              />
            </Link>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: '1.6', marginBottom: '20px' }}>
              Turning raw accounting into an intelligent operating system. Bringing Fortune-500 level financial intelligence to growth-stage Startups and MSMEs.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--signal-green)', fontSize: '0.85rem', fontWeight: 500 }}>
              <ShieldCheck size={16} />
              <span>Chartered Accountant Firm Backed</span>
            </div>
          </div>

          {/* Col 2: Navigation */}
          <div>
            <h4
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-primary)',
                marginBottom: '18px',
              }}
            >
              Platform
            </h4>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li>
                <Link to="/solutions" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}>
                  Industry Solutions
                </Link>
              </li>
              <li>
                <Link to="/what-we-do" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}>
                  What We Do
                </Link>
              </li>
              <li>
                <Link to="/diagnostic" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'color 0.2s' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}>
                  12-Point Diagnostic
                  <span className="badge-pill badge-red" style={{ fontSize: '0.65rem', padding: '2px 8px' }}>Free</span>
                </Link>
              </li>
              <li>
                <Link to="/intelligence" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}>
                  From Accounting to Intelligence
                </Link>
              </li>
              <li>
                <Link to="/about-us" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')} onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}>
                  About Us (The 42 Story)
                </Link>
              </li>
              <li>
                <a href="https://dash.forty2.online/login" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-red-light)', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  Client Portal <ArrowUpRight size={14} />
                </a>
              </li>
            </ul>
          </div>

          {/* Col 3: Regional Markets */}
          <div>
            <h4
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-primary)',
                marginBottom: '18px',
              }}
            >
              Active Markets
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                <Globe size={16} color="var(--brand-red)" />
                <span>India (National & Multi-State GST)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                <Globe size={16} color="var(--brand-red)" />
                <span>Middle East (GCC Compliance & VAT)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                <Globe size={16} color="var(--brand-red)" />
                <span>Southeast Asia (Cross-border Delivery)</span>
              </div>
            </div>

            <div style={{ marginTop: '20px', padding: '12px 14px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Integrations Supported</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>Tally Prime · Zoho Books · QuickBooks</span>
            </div>
          </div>

          {/* Col 4: Direct Inquiries */}
          <div>
            <h4
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-primary)',
                marginBottom: '18px',
              }}
            >
              Contact Direct
            </h4>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li>
                <a
                  href="mailto:ksr@forty2.online"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                >
                  <Mail size={16} color="var(--brand-red)" />
                  <span>ksr@forty2.online</span>
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/919789367892?text=Hi%20Forty2%20Team%2C%20I%20would%20like%20to%20learn%20more%20about%20Financial%20Intelligence"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                >
                  <Phone size={16} color="var(--signal-green)" />
                  <span>+91-9789 FORTY 2 (+91 97893 67892)</span>
                </a>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Link to="/contact" className="btn-secondary" style={{ padding: '8px 16px', fontSize: '0.85rem', width: 'fit-content' }}>
                  Open Inquiry Form
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          style={{
            paddingTop: '28px',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            fontSize: '0.82rem',
            color: 'var(--text-muted)',
          }}
        >
          <div>
            © {new Date().getFullYear()} Forty2 (४२). All rights reserved. Finance Made Intelligent.
          </div>
          <div style={{ display: 'flex', gap: '20px' }}>
            <span>Platform Agnostic</span>
            <span>•</span>
            <span>Chartered Accountant Rigor</span>
            <span>•</span>
            <span>AI Automated</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
